import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Grid,
  Avatar,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress
} from "@mui/material";
import { 
  PersonAdd as PersonAddIcon,
  List as ListIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  People as PeopleIcon,
  GroupWork as GroupWorkIcon,
  ChildCare as ChildCareIcon,
  LocalHospital as LocalHospitalIcon,
  ArrowForward as ArrowForwardIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Event as EventIcon
} from "@mui/icons-material";
import crecheApi from "../../services/crecheApi";

export default function DashboardCrechePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [grupos, setGrupos] = useState([]);
  const [matriculas, setMatriculas] = useState([]);

  useEffect(() => {
    carregarDados();
  }, []);

  async function carregarDados() {
    try {
      setLoading(true);
      const [grupos, matriculas] = await Promise.all([
        crecheApi.listarGrupos(),
        crecheApi.listarMatriculas()
      ]);
      setGrupos(grupos);
      setMatriculas(matriculas);
    } catch (err) {
      console.error("Erro ao carregar dados do dashboard:", err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '70vh',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          A carregar painel da creche...
        </Typography>
      </Box>
    );
  }

  // Cálculos
  const ativas = matriculas.filter(m => m.status === "ATIVA").length;
  const pendentes = matriculas.filter(m => m.status === "AGUARDANDO_PAGAMENTO").length;
  const totalVagas = grupos.reduce((sum, g) => sum + g.capacidade, 0);
  const ocupadas = matriculas.length;
  const vagasDisponiveis = totalVagas - ocupadas;
  const taxaOcupacao = totalVagas > 0 ? Math.round((ocupadas / totalVagas) * 100) : 0;

  // Cards de ação rápida
  const acoes = [
    {
      titulo: "Check-ins",
      descricao: "Registrar entradas e saídas diárias",
      icon: <AccessTimeIcon sx={{ fontSize: 40 }} />,
      cor: "#1976d2",
      rota: "/creche/checkins",
      badge: "Hoje"
    },
    {
      titulo: "Presenças",
      descricao: "Marcar presença e chamada diária",
      icon: <CheckCircleIcon sx={{ fontSize: 40 }} />,
      cor: "#2e7d32",
      rota: "/creche/presencas",
      badge: null
    },
    {
      titulo: "Matrículas",
      descricao: "Gerir matrículas de crianças",
      icon: <ChildCareIcon sx={{ fontSize: 40 }} />,
      cor: "#ed6c02",
      rota: "/creche/matriculas",
      badge: pendentes > 0 ? `${pendentes} pendentes` : null
    },
    {
      titulo: "Responsáveis",
      descricao: "Gerir pais e encarregados",
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      cor: "#9c27b0",
      rota: "/creche/responsaveis",
      badge: null
    },
    {
      titulo: "Saúde",
      descricao: "Fichas médicas e alergias",
      icon: <LocalHospitalIcon sx={{ fontSize: 40 }} />,
      cor: "#d32f2f",
      rota: "/creche/saude",
      badge: null
    },
    {
      titulo: "Relatórios",
      descricao: "Estatísticas e análises",
      icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
      cor: "#0288d1",
      rota: "/creche/relatorios",
      badge: null
    }
  ];

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Cabeçalho */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4 
      }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1976d2' }}>
            🏫 Gestão de Creche
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Painel de controlo e acesso rápido
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title="Atualizar dados">
            <IconButton onClick={carregarDados} color="primary" sx={{ bgcolor: 'white' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button 
            variant="contained" 
            startIcon={<PersonAddIcon />}
            size="large"
            onClick={() => navigate('/creche/matriculas/nova')}
            sx={{ 
              boxShadow: 3,
              textTransform: 'none',
              fontSize: '1rem',
              px: 3
            }}
          >
            Nova Matrícula
          </Button>
        </Box>
      </Box>

      {/* Estatísticas Principais */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            boxShadow: 3
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                    Matrículas Ativas
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {ativas}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', width: 60, height: 60 }}>
                  <ChildCareIcon sx={{ fontSize: 35 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            boxShadow: 3
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                    Vagas Disponíveis
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {vagasDisponiveis}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    de {totalVagas} vagas totais
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', width: 60, height: 60 }}>
                  <EventIcon sx={{ fontSize: 35 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            boxShadow: 3
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                    Taxa de Ocupação
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {taxaOcupacao}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={taxaOcupacao} 
                    sx={{ 
                      mt: 1, 
                      bgcolor: 'rgba(255,255,255,0.3)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: 'white'
                      }
                    }} 
                  />
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', width: 60, height: 60 }}>
                  <TrendingUpIcon sx={{ fontSize: 35 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            background: pendentes > 0 
              ? 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
              : 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
            color: 'white',
            boxShadow: 3
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                    Pagamentos Pendentes
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {pendentes}
                  </Typography>
                  {pendentes > 0 && (
                    <Chip 
                      label="Requer atenção" 
                      size="small" 
                      sx={{ 
                        mt: 1,
                        bgcolor: 'rgba(255,255,255,0.3)',
                        color: 'white'
                      }}
                    />
                  )}
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', width: 60, height: 60 }}>
                  <WarningIcon sx={{ fontSize: 35 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Ações Rápidas */}
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Acesso Rápido
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {acoes.map((acao, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card 
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}
              onClick={() => navigate(acao.rota)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: acao.cor, 
                    width: 60, 
                    height: 60,
                    boxShadow: 2
                  }}>
                    {acao.icon}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {acao.titulo}
                      </Typography>
                      {acao.badge && (
                        <Chip 
                          label={acao.badge} 
                          size="small" 
                          color="primary"
                          sx={{ height: 20 }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {acao.descricao}
                    </Typography>
                  </Box>
                  <IconButton size="small" sx={{ alignSelf: 'center' }}>
                    <ArrowForwardIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Grupos e Ocupação */}
      <Card sx={{ boxShadow: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <GroupWorkIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Grupos e Ocupação
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Distribuição de crianças por faixa etária
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            {grupos.map((grupo) => {
              const ocupacao = matriculas.filter(m => m.grupoId === grupo.id).length;
              const percentual = grupo.capacidade > 0 
                ? Math.round((ocupacao / grupo.capacidade) * 100) 
                : 0;

              return (
                <Grid item xs={12} sm={6} md={3} key={grupo.id}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        {grupo.nome}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {grupo.faixaEtariaMin} - {grupo.faixaEtariaMax} anos
                      </Typography>
                      <Box sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            Ocupação
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {ocupacao} / {grupo.capacidade}
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={percentual}
                          sx={{ 
                            height: 8, 
                            borderRadius: 5,
                            bgcolor: 'grey.200'
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {percentual}% ocupado
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}